import moment from "moment";
import Format from "../Format";
export default class Patient {
  /**
   * Returns the patient's display name, filtering out empty parts and using
   * only the capitalized middle initial.
   * @param {Object} patient - The patient object.
   * @returns {string} The formatted display name.
   */
  static getDisplayName(patient) {
    const firstName = patient.firstName ? patient.firstName.trim() : "";
    const middleInitial =
      patient.middleName && patient.middleName.trim()
        ? patient.middleName.trim()[0].toUpperCase()
        : "";
    const lastName = patient.lastName ? patient.lastName.trim() : "";
    const nameParts = [firstName, middleInitial, lastName].filter(Boolean);
    return nameParts.join(" ");
  }

  /**
   * Get the demographic summary of a patient.
   * @param {Object} patient - The patient object.
   * @returns {JSX.Element} The demographic summary.
   */
  static getDemographicSummary(patient, termsIndexed) {
    let referenceDate;
    const dateOfBirth = patient.dateOfBirth
      ? moment(patient.dateOfBirth)
      : null;
    const dateOfDeath = patient.dateOfDeath
      ? moment(patient.dateOfDeath)
      : null;

    if (patient.deceased) {
      referenceDate = dateOfDeath
        ? dateOfDeath
        : dateOfBirth
          ? dateOfBirth
              .clone()
              .add(moment().diff(dateOfBirth, "years"), "years")
          : moment();
    } else {
      referenceDate = moment();
    }

    const ageInYears = dateOfBirth
      ? referenceDate.diff(dateOfBirth, "years")
      : "";
    const ageInMonths = dateOfBirth
      ? referenceDate.diff(dateOfBirth, "months")
      : "";
    const ageDisplay =
      ageInYears !== "" && ageInYears < 2
        ? `${ageInMonths}mo`
        : `${ageInYears}y`;

    const elements = [];
    if (patient.deceased) {
      elements.push(
        `${dateOfBirth ? dateOfBirth.format("M/D/YYYY") : ""} - ${dateOfDeath ? dateOfDeath.format("M/D/YYYY") + ` (${ageDisplay})` : "Deceased"}`,
      );
    } else {
      elements.push(
        `${dateOfBirth ? dateOfBirth.format("M/D/YYYY") : ""} (${ageDisplay})`,
      );
    }
    if (patient.gender) {
      elements.push(
        patient.gender === "Other" ? "X" : patient.gender.charAt(0),
      );
    }
    // if (patient.preferredPronounsTermId) {
    //     elements.push( patient.preferredPronounsTermId === 176 && patient.preferredPronounsOther !== null && patient.preferredPronounsOther !== "" ? patient.preferredPronounsOther : Object.values(termsIndexed || {}).find(term => term.id === patient.preferredPronounsTermId)?.name); // 176 is the id for "Other" in the preferred pronouns list
    // }
    if (patient.height) {
      elements.push(
        `${Math.floor(patient.height / 12)}' ${patient.height % 12}\"`,
      );
    }
    if (patient.weight) {
      elements.push(`${patient.weight}lbs`);
    }
    return elements.join(" • ");
  }

  /**
   * Get collection issues for a patient.
   * @param {Object} patient The patient
   * @param {Object} patientIssues The patient issues in chartContext
   * @param {Object} issueReasons The indexed issue reasons in mainContext
   * @returns {Object|undefined} The collection issues or undefined if none.
   */
  static getServiceDeniedReasons(patient, patientIssues, issueReasons) {
    let USDollar = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    });
    const deniedReasons = new Set();

    const patientCollectionBalance = (patient.collectionsBalance ?? 0) > 0;

    if (patientCollectionBalance) {
      deniedReasons.add(
        `Collections: ${USDollar.format(patient.collectionsBalance)}`,
      );
    }

    Object.values(patientIssues).forEach((patientIssue) => {
      let issueReason = issueReasons[patientIssue.issueReasonId] || null;

      if (
        issueReason &&
        issueReason.preventService &&
        patientIssue.issueStatus !== 1
      ) {
        deniedReasons.add(`${issueReason.issueCategory?.name}`);
      }
    });

    return Array.from(deniedReasons);
  }

  /**
   * Get the Brightree URL for a patient.
   * @param {*} patient The patient.
   * @returns {string} The Brightree URL for the patient.
   */
  static getBrightreeUrl(patient) {
    return `https://brightree.net/F1/02200/OrbitMedical/Patient/frmPatientSummary.aspx?PatientKey=${patient.brightreeExternalKey}&showAck=1`;
  }

  /**
   * Returns the userprofile (sales rep) for the most recent non-canceled,
   * persisted 'New' order for the patient in the chart. Falls back to the
   * account owner if no such order exists.
   * @param {Object} chart - The chart object containing orderActivity,
   * activity, patient, account.
   * @param {Object} usersIndexed - Map of user id to userprofile object.
   * @returns {Object|null} The userprofile object or null if not found.
   */
  static getSalesRepUserprofile(chart, usersIndexed) {
    const mostRecentNewOrderActivity =
      Patient.getMostRecentNewOrderActivity(chart);
    if (mostRecentNewOrderActivity) {
      return usersIndexed[
        chart.activity[mostRecentNewOrderActivity.id].ownerId
      ];
    } else {
      // Fall back to patient account
      const patient = Object.values(chart.patient)[0];
      const account = chart.account[patient.accountId];
      return account ? usersIndexed[account.ownerId] : null;
    }
  }

  /**
   * Returns the location for the most recent non-canceled, persisted 'New'
   * order for the patient in the chart. Falls back to the account location if
   * no such order exists.
   * @param {Object} chart - The chart object containing orderActivity,
   * activity, patient, account.
   * @param {Object} locationsIndexed - Map of location id to location object.
   * @returns {Object|null} The location object or null if not found.
   */
  static getLocation(chart, locationsIndexed) {
    const mostRecentNewOrderActivity =
      Patient.getMostRecentNewOrderActivity(chart);
    if (mostRecentNewOrderActivity) {
      return locationsIndexed[mostRecentNewOrderActivity.salesLocationId];
    } else {
      // Fall back to patient account
      const patient = Object.values(chart.patient)[0];
      const account = chart.account[patient.accountId];
      return account ? locationsIndexed[account.loactionId] : null;
    }
  }

  /**
   * Returns the most recent non-canceled, persisted 'New' order activity for
   * the patient in the chart. Excludes orders with negative ids (not
   * persisted).
   * @param {Object} chart - The chart object containing orderActivity,
   * activity, patient.
   * @returns {Object|null} The most recent orderActivity object or null if
   * not found.
   */
  static getMostRecentNewOrderActivity(chart) {
    const filteredOrderActivities = Object.values(chart.orderActivity).filter(
      (orderActivity) =>
        orderActivity.orderType?.toLowerCase() === "new" &&
        orderActivity.id > 0,
    );

    filteredOrderActivities.sort((a, b) => {
      const aStarted = chart.activity[a.id]?.startedAt
        ? new Date(chart.activity[a.id].startedAt).getTime()
        : 0;
      const bStarted = chart.activity[b.id]?.startedAt
        ? new Date(chart.activity[b.id].startedAt).getTime()
        : 0;
      return bStarted - aStarted;
    });

    if (filteredOrderActivities.length > 0) {
      return filteredOrderActivities[0];
    }

    return null;
  }

  /**
   * Returns the uploaded file ID of the most recent eligible Medicare Rx file
   * for the patient. Eligibility criteria:
   * - File name contains "-signed_rx."
   * - Uploaded after deployment date ("2025-08-08")
   * - Associated insurance type is Medicare (1) or Medicare Replacement (8)
   * - Service reason is not "New Accessory After The Sale"
   * - Uploaded within the last 12 months
   * @param {Object} chart - The chart object containing uploadedFile,
   * activityFile, and orderActivity.
   * @returns {string|null} The uploaded file ID, or null if none found.
   */
  static getLastMedicareRxUploadedFileId(chart) {
    if (!chart?.uploadedFile || !chart?.activityFile || !chart?.orderActivity) {
      return null;
    }

    // Filter uploaded files to only include files that have an activity file and are not deleted
    const uploadedFiles = Object.values(chart.uploadedFile).filter(
      (uploadedFile) =>
        Object.values(chart.activityFile).some(
          (activityFile) =>
            activityFile.fileId === uploadedFile.id &&
            activityFile.deleted !== true,
        ),
    );

    // Only consider files named "-signed_rx." after deployment date
    const uploadedFilesRx = uploadedFiles.filter(
      (uploadedFile) =>
        uploadedFile.filename.includes("-signed_rx.") &&
        moment(uploadedFile.uploadedAt).isSameOrAfter("2025-08-08", "day"),
    );

    // Map to files with Medicare/Medicare Replacement insurance and not "New Accessory After The Sale"
    const uploadedFilesEligible = uploadedFilesRx.filter((uploadedFile) => {
      const matchingActivityFile = Object.values(chart.activityFile).find(
        (activityFile) => activityFile.fileId === uploadedFile.id,
      );

      const orderId = matchingActivityFile?.activityId;
      const orderActivity = orderId ? chart.orderActivity[orderId] : null;
      const hasMedicareOrMedicareReplacement =
        orderActivity?.insuranceTypeId === 1 ||
        orderActivity?.insuranceTypeId === 8;

      // Check service reason directly from orderActivity if available
      const newAccessoryAfterSale =
        orderActivity?.serviceReason === "New Accessory After The Sale";

      return hasMedicareOrMedicareReplacement && !newAccessoryAfterSale;
    });

    // Find most recent within last 12 months
    let uploadedFileId = null;
    const twelveMonthsAgo = moment().subtract(12, "months");

    uploadedFilesEligible.forEach((uploadedFile) => {
      const rxDate = moment(uploadedFile.uploadedAt);
      if (rxDate.isBefore(twelveMonthsAgo)) {
        return;
      }
      if (
        !uploadedFileId ||
        rxDate.isAfter(moment(chart.uploadedFile[uploadedFileId]?.uploadedAt))
      ) {
        uploadedFileId = uploadedFile.id;
      }
    });

    return uploadedFileId || null;
  }

  /**
   * Returns the uploaded file ID of the most recent chart notes file for the
   * patient. Criteria:
   * - File name contains "chart" (case-insensitive)
   * - File has an associated activity file and is not deleted
   * @param {Object} chart - The chart object containing uploadedFile,
   * activityFile, and orderActivity.
   * @returns {string|null} The uploaded file ID, or null if none found.
   */
  static getLastChartNotesUploadedFileId(chart) {
    if (!chart?.uploadedFile || !chart?.activityFile || !chart?.orderActivity) {
      return null;
    }

    // Filter uploaded files to only include files that have an activity file and are not deleted
    const uploadedFiles = Object.values(chart.uploadedFile).filter(
      (uploadedFile) =>
        Object.values(chart.activityFile).some(
          (activityFile) =>
            activityFile.fileId === uploadedFile.id &&
            activityFile.deleted !== true,
        ),
    );

    // Only consider files whose filename contains "chart"
    const uploadedFilesChart = uploadedFiles.filter((uploadedFile) =>
      uploadedFile.filename.toLowerCase().includes("chart"),
    );

    // Find most recent by uploadedAt
    let uploadedFileId = null;
    uploadedFilesChart.forEach((uploadedFile) => {
      if (
        !uploadedFileId ||
        moment(uploadedFile.uploadedAt).isAfter(
          moment(chart.uploadedFile[uploadedFileId]?.uploadedAt),
        )
      ) {
        uploadedFileId = uploadedFile.id;
      }
    });

    return uploadedFileId || null;
  }

  /**
   * Returns the uploaded file ID of the most recent file of a given activity
   * file type for the patient. Criteria:
   * - File has an associated activity file of the given type
   * - File is not deleted
   * @param {Object} chart - The chart object containing uploadedFile and
   * activityFile.
   * @param {number} activityFileTypeTermId - The activity file type term ID
   * to match on.
   * @returns {string|null} The uploaded file ID, or null if none found.
   */
  static getLastUploadedFileIdByActivityFileType(
    chart,
    activityFileTypeTermId,
  ) {
    const uploadedFiles = Object.values(chart.uploadedFile).filter(
      (uploadedFile) =>
        Object.values(chart.activityFile).some(
          (activityFile) =>
            activityFile.fileId === uploadedFile.id &&
            activityFile.activityFileTypeTermId === activityFileTypeTermId &&
            activityFile.deleted !== true,
        ),
    );

    // Most recently uploaded file first
    uploadedFiles.sort((a, b) =>
      moment(b.uploadedAt).diff(moment(a.uploadedAt)),
    );

    return uploadedFiles.length > 0 ? uploadedFiles[0].id : null;
  }

  /**
   * Returns the uploaded file ID of the most recent "Appointment of
   * Representative" file for the patient. Criteria:
   * - File name contains "appointment_of_representative" (case-insensitive)
   * - Uploaded within the last year
   * - File has an associated activity file that is not deleted
   * @param {Object} chart - The chart object containing uploadedFile and
   * activityFile.
   * @returns {string|null} The uploaded file ID, or null if none found.
   */
  static getLastAppointmentOfRepresentativeUploadedFileId(chart) {
    if (!chart?.uploadedFile) {
      return null;
    }

    const oneYearAgo = moment().subtract(1, "years");
    const uploadedFiles = Object.values(chart.uploadedFile).filter(
      (uploadedFile) =>
        uploadedFile.filename
          .toLowerCase()
          .includes("appointment_of_representative") &&
        moment(uploadedFile.uploadedAt).isSameOrAfter(oneYearAgo) &&
        Object.values(chart.activityFile).some(
          (activityFile) =>
            activityFile.fileId === uploadedFile.id &&
            activityFile.deleted !== true,
        ),
    );

    let uploadedFileId = null;
    uploadedFiles.forEach((uploadedFile) => {
      if (
        !uploadedFileId ||
        moment(uploadedFile.uploadedAt).isAfter(
          moment(chart.uploadedFile[uploadedFileId]?.uploadedAt),
        )
      ) {
        uploadedFileId = uploadedFile.id;
      }
    });

    return uploadedFileId || null;
  }
}
